import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MdClose, MdAccessTime, MdPeople, MdEventNote, MdLocationOn, MdMeetingRoom, MdRepeat, MdCalendarToday, MdCheck, MdWarning } from 'react-icons/md';
import { conferenceRoomApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import './BookingModal.css';

// BookingModal Component
// isUserView prop: when true, applies 1-hour advance booking validation (user side only)
function BookingModal({ isOpen, onClose, selectedRoom, onBookingSuccess, isUserView = false }) {
  const [formData, setFormData] = useState({
    startDateTime: null,
    endDateTime: null,
    purpose: '',
    attendeeCount: '',
    // Recurring booking fields (always weekly)
    isRecurring: false,
    daysOfWeek: [],
    recurringEndDate: null
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch available slots when start date changes
  useEffect(() => {
    if (formData.startDateTime && selectedRoom) {
      fetchAvailableSlots(formData.startDateTime);
    }
  }, [formData.startDateTime, selectedRoom]);

  // Fetch booked slots for the selected date
  const fetchAvailableSlots = async (date) => {
    try {
      setIsLoading(true);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      const response = await conferenceRoomApi.getAvailableSlots(selectedRoom.id, dateStr);
      setAvailableSlots(response.data || response || []);
    } catch (error) {
      setAvailableSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if time slot is available
  const isTimeSlotAvailable = (startTime, endTime) => {
    if (!availableSlots || !Array.isArray(availableSlots) || availableSlots.length === 0) return true;

    const start = new Date(startTime).getTime();
    const end = new Date(endTime).getTime();

    // Check if selected time overlaps with any booked slot
    for (const slot of availableSlots) {
      const slotStart = new Date(slot.startTime).getTime();
      const slotEnd = new Date(slot.endTime).getTime();

      // If there's any overlap, slot is unavailable
      if ((start >= slotStart && start < slotEnd) ||
          (end > slotStart && end <= slotEnd) ||
          (start <= slotStart && end >= slotEnd)) {
        return false;
      }
    }
    return true;
  };

  // Check if the room type is Medical (exempt from 1-hour advance booking rule)
  const isMedicalRoom = selectedRoom?.roomType?.toLowerCase().includes('medical');

  // Filter time slots - Hide past times and for users, hide times within next hour (except for Medical rooms)
  const filterPassedTime = (time) => {
    const currentDate = new Date();
    const selectedDate = new Date(time);

    // For medical rooms: allow the current 30-min slot (the slot that contains "now")
    // so employees can book immediately without waiting for the next interval
    if (isMedicalRoom) {
      const currentSlotStart = new Date(currentDate);
      currentSlotStart.setMinutes(currentDate.getMinutes() < 30 ? 0 : 30, 0, 0);
      return selectedDate >= currentSlotStart;
    }

    // Don't allow past times for non-medical rooms
    if (selectedDate < currentDate) {
      return false;
    }

    // For user view, don't allow times within the next hour
    if (isUserView) {
      const oneHourFromNow = new Date(currentDate.getTime() + 60 * 60 * 1000);
      return selectedDate >= oneHourFromNow;
    }

    return true;
  };

  // Check if a specific time is booked
  const isTimeBooked = (time) => {
    if (!availableSlots || !Array.isArray(availableSlots) || availableSlots.length === 0) return false;

    const selectedTime = new Date(time);

    for (const slot of availableSlots) {
      const slotStart = new Date(slot.startTime);
      const slotEnd = new Date(slot.endTime);

      // Check if the time falls within a booked slot
      if (selectedTime >= slotStart && selectedTime < slotEnd) {
        return true;
      }
    }
    return false;
  };

  // Get booked times to grey out in the picker
  const getExcludedTimes = () => {
    if (!formData.startDateTime || !availableSlots || !Array.isArray(availableSlots) || availableSlots.length === 0) {
      return [];
    }

    const excludedTimes = [];
    const selectedDate = formData.startDateTime;

    // Generate 30-minute intervals for the selected day
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeToCheck = new Date(selectedDate);
        timeToCheck.setHours(hour, minute, 0, 0);

        // Check if this time is within any booked slot
        if (isTimeBooked(timeToCheck)) {
          excludedTimes.push(timeToCheck);
        }
      }
    }

    return excludedTimes;
  };

  // Handle date picker change with validation
  const handleDateTimeChange = (field, date) => {
    if (field === 'startDateTime' && date && isTimeBooked(date)) {
      toast.warning('This time slot is already booked! Please select another time.', {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    handleChange(field, date);
  };

  // Filter end time - must be after start time
  const filterEndTime = (time) => {
    if (!formData.startDateTime) return true;

    const selectedEndTime = new Date(time);
    const startTime = new Date(formData.startDateTime);

    // End time must be after start time
    return selectedEndTime > startTime;
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user makes changes
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle duration preset click
  const handleDurationPreset = (minutes) => {
    if (!formData.startDateTime) {
      toast.warning('Please select a start date and time first', {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    const endDateTime = new Date(formData.startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + minutes);

    handleChange('endDateTime', endDateTime);
  };

  // Calculate duration for display
  const calculateDuration = () => {
    if (!formData.startDateTime || !formData.endDateTime) return null;

    const start = new Date(formData.startDateTime);
    const end = new Date(formData.endDateTime);
    const diffMs = end - start;

    if (diffMs <= 0) return null;

    const diffMins = Math.floor(diffMs / 60000);
    const days = Math.floor(diffMins / 1440);
    const hours = Math.floor((diffMins % 1440) / 60);
    const minutes = diffMins % 60;

    let durationStr = '';
    if (days > 0) durationStr += `${days}d `;
    if (hours > 0) durationStr += `${hours}h `;
    if (minutes > 0) durationStr += `${minutes}m`;

    return durationStr.trim() || 'Less than 1 minute';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.startDateTime) {
      newErrors.startDateTime = 'Please select start date and time';
    }

    if (!formData.endDateTime) {
      newErrors.endDateTime = 'Please select end date and time';
    }

    if (!formData.purpose || formData.purpose.trim() === '') {
      newErrors.purpose = 'Please enter the purpose of booking';
    }

    // Validate attendee count
    if (formData.attendeeCount && formData.attendeeCount !== '') {
      const count = parseInt(formData.attendeeCount);
      if (isNaN(count) || count <= 0) {
        newErrors.attendeeCount = 'Attendee count must be a positive number';
      } else if (selectedRoom?.capacity && count > selectedRoom.capacity) {
        newErrors.attendeeCount = `Exceeds room capacity (${selectedRoom.capacity})`;
      }
    }

    // User-side validation: Booking must be at least 1 hour in advance (except for Medical rooms)
    if (isUserView && !isMedicalRoom && formData.startDateTime) {
      const now = new Date();
      const start = new Date(formData.startDateTime);
      const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);

      if (start < oneHourFromNow) {
        newErrors.startDateTime = 'Booking must be made at least 1 hour in advance';
      }
    }

    // Validate recurring booking fields
    if (formData.isRecurring) {
      if (!formData.daysOfWeek || formData.daysOfWeek.length === 0) {
        newErrors.daysOfWeek = 'Please select at least one day';
      }
      if (!formData.recurringEndDate) {
        newErrors.recurringEndDate = 'Please select an end date';
      } else if (formData.startDateTime) {
        const seriesDays = (formData.recurringEndDate - new Date(formData.startDateTime.toDateString())) / (1000 * 60 * 60 * 24);
        if (seriesDays > 7) {
          newErrors.recurringEndDate = 'Recurring series cannot span more than 1 week (7 days)';
        }
      }
    }

    // Check if end time is valid
    if (formData.startDateTime && formData.endDateTime) {
      const start = new Date(formData.startDateTime);
      const end = new Date(formData.endDateTime);

      if (end <= start) {
        newErrors.endDateTime = 'End time must be after start time';
      }

      // Medical room: same-day only and max 45 minutes
      if (isMedicalRoom) {
        const durationMins = (end - start) / 60000;
        if (start.toDateString() !== end.toDateString()) {
          newErrors.endDateTime = 'Medical room bookings must start and end on the same day';
        } else if (durationMins > 45) {
          newErrors.endDateTime = 'Medical room bookings cannot exceed 45 minutes';
        }
      } else {
        // Regular rooms: max 1 week (7 days)
        const durationDays = (end - start) / (1000 * 60 * 60 * 24);
        if (durationDays > 7) {
          newErrors.endDateTime = 'Bookings cannot exceed 1 week (7 days) duration';
        }
      }

      // Check if slot is available
      if (!isTimeSlotAvailable(start, end)) {
        newErrors.startDateTime = 'This time slot overlaps with an existing booking';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Format datetime for backend (local timezone without UTC conversion)
  const formatDateTimeForBackend = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      let response;

      if (formData.isRecurring) {
        // Recurring booking
        const recurringData = {
          roomId: selectedRoom.id,
          startTime: formatDateTimeForBackend(formData.startDateTime),
          endTime: formatDateTimeForBackend(formData.endDateTime),
          notes: formData.purpose,
          attendeeCount: formData.attendeeCount ? parseInt(formData.attendeeCount) : null,
          daysOfWeek: formData.daysOfWeek,
          endDate: formData.recurringEndDate ? formData.recurringEndDate.toISOString().split('T')[0] : null
        };

        response = await conferenceRoomApi.createRecurringBooking(recurringData);
      } else {
        // Single booking
        const bookingData = {
          roomId: selectedRoom.id,
          startTime: formatDateTimeForBackend(formData.startDateTime),
          endTime: formatDateTimeForBackend(formData.endDateTime),
          notes: formData.purpose,
          attendeeCount: formData.attendeeCount ? parseInt(formData.attendeeCount) : null
        };

        response = await conferenceRoomApi.createBooking(bookingData);
      }

      // Check if backend returned a validation error (HTTP 200 with success: false)
      if (response && response.success === false) {
        toast.error(response.error || response.message || 'Booking validation failed', {
          position: "top-right",
          autoClose: 5000,
        });
        return;
      }

      toast.success(
        formData.isRecurring
          ? 'Recurring booking created! Awaiting admin approval.'
          : 'Booking request submitted! Awaiting admin approval.',
        { position: "top-right", autoClose: 4000 }
      );

      // Reset form and close modal
      resetForm();
      onBookingSuccess && onBookingSuccess();
      onClose();

    } catch (error) {
      let errorMsg = 'Failed to book room';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        if (status === 409) {
          errorMsg = typeof data === 'string' ? data : (data?.message || 'Time slot conflict detected');
        } else if (typeof data === 'string') {
          errorMsg = data;
        } else if (data?.message) {
          errorMsg = data.message;
        } else if (data?.error) {
          errorMsg = data.error;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      startDateTime: null,
      endDateTime: null,
      purpose: '',
      attendeeCount: '',
      isRecurring: false,
      daysOfWeek: [],
      recurringEndDate: null
    });
    setErrors({});
  };

  // Handle modal close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const duration = calculateDuration();
  const isAvailable = formData.startDateTime && formData.endDateTime &&
                      isTimeSlotAvailable(formData.startDateTime, formData.endDateTime);

  const daysOfWeekOptions = [
    { value: 'MONDAY', label: 'Mon' },
    { value: 'TUESDAY', label: 'Tue' },
    { value: 'WEDNESDAY', label: 'Wed' },
    { value: 'THURSDAY', label: 'Thu' },
    { value: 'FRIDAY', label: 'Fri' },
    { value: 'SATURDAY', label: 'Sat' },
    { value: 'SUNDAY', label: 'Sun' }
  ];

  return (
    <div className="booking-modal-overlay" onClick={handleClose}>
      <div className="booking-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="booking-modal-header">
          <div className="header-content">
            <MdCalendarToday className="header-icon" />
            <h2>Book Workspace</h2>
          </div>
          <button className="close-btn" onClick={handleClose}>
            <MdClose />
          </button>
        </div>

        {/* Room Info Card */}
        <div className="room-info-card">
          <div className="room-info-main">
            <div className="room-icon">
              <MdMeetingRoom />
            </div>
            <div className="room-details">
              <h3>{selectedRoom?.roomName}</h3>
              <div className="room-meta">
                <span className="meta-item">
                  <MdMeetingRoom />
                  {selectedRoom?.roomType}
                </span>
                <span className="meta-item">
                  <MdLocationOn />
                  {selectedRoom?.tower}, {selectedRoom?.floor}
                </span>
                {selectedRoom?.capacity && (
                  <span className="meta-item capacity">
                    <MdPeople />
                    {selectedRoom.capacity} people
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="booking-form">
          {/* Start Date & Time */}
          <div className="form-section">
            <label className="form-label">
              <MdAccessTime className="label-icon" />
              Start Date & Time <span className="required">*</span>
            </label>
            <DatePicker
              selected={formData.startDateTime}
              onChange={(date) => handleDateTimeChange('startDateTime', date)}
              showTimeSelect
              timeIntervals={30}
              timeCaption="Time"
              dateFormat="MMM d, yyyy  h:mm aa"
              minDate={new Date()}
              filterTime={filterPassedTime}
              excludeTimes={getExcludedTimes()}
              placeholderText="Select start date and time"
              className={`form-input ${errors.startDateTime ? 'error' : ''}`}
              autoComplete="off"
            />
            {errors.startDateTime && <span className="error-text">{errors.startDateTime}</span>}
            <span className="helper-text">
              {isMedicalRoom
                ? 'Medical room: same day only, max 45 minutes'
                : isUserView
                  ? 'Bookings must be made at least 1 hour in advance (max 7 days)'
                  : 'Max booking duration: 7 days. Greyed out times are unavailable'}
            </span>
          </div>

          {/* Quick Duration Selection */}
          {formData.startDateTime && (
            <div className="form-section">
              <label className="form-label">Quick Duration</label>
              <div className="duration-presets">
                {(isMedicalRoom
                  ? [
                      { mins: 15, label: '15m' },
                      { mins: 30, label: '30m' },
                      { mins: 45, label: '45m' }
                    ]
                  : [
                      { mins: 30, label: '30m' },
                      { mins: 60, label: '1h' },
                      { mins: 90, label: '1.5h' },
                      { mins: 120, label: '2h' },
                      { mins: 240, label: '4h' }
                    ]
                ).map(({ mins, label }) => (
                  <button
                    key={mins}
                    type="button"
                    className="duration-btn"
                    onClick={() => handleDurationPreset(mins)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* End Date & Time */}
          <div className="form-section">
            <label className="form-label">
              <MdAccessTime className="label-icon" />
              End Date & Time <span className="required">*</span>
            </label>
            <DatePicker
              selected={formData.endDateTime}
              onChange={(date) => handleChange('endDateTime', date)}
              showTimeSelect
              timeIntervals={30}
              timeCaption="Time"
              dateFormat="MMM d, yyyy  h:mm aa"
              minDate={formData.startDateTime || new Date()}
              filterTime={filterEndTime}
              placeholderText="Select end date and time"
              className={`form-input ${errors.endDateTime ? 'error' : ''}`}
              autoComplete="off"
              disabled={!formData.startDateTime}
            />
            {errors.endDateTime && <span className="error-text">{errors.endDateTime}</span>}
            {!formData.startDateTime && <span className="helper-text">Please select start time first</span>}
          </div>

          {/* Duration Display */}
          {duration && (
            <div className={`duration-display ${isAvailable ? 'available' : 'unavailable'}`}>
              <div className="duration-info">
                <MdAccessTime />
                <span className="duration-value">{duration}</span>
              </div>
              <div className="availability-badge">
                {isAvailable ? (
                  <>
                    <MdCheck />
                    <span>Available</span>
                  </>
                ) : (
                  <>
                    <MdWarning />
                    <span>Conflict</span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Purpose */}
          <div className="form-section">
            <label className="form-label">
              <MdEventNote className="label-icon" />
              Purpose <span className="required">*</span>
            </label>
            <textarea
              value={formData.purpose}
              onChange={(e) => handleChange('purpose', e.target.value)}
              placeholder="e.g., Team Meeting, Client Presentation, Training Session"
              rows="2"
              className={`form-input form-textarea ${errors.purpose ? 'error' : ''}`}
            />
            {errors.purpose && <span className="error-text">{errors.purpose}</span>}
          </div>

          {/* Number of Attendees */}
          <div className="form-section">
            <label className="form-label">
              <MdPeople className="label-icon" />
              Number of Attendees
            </label>
            <input
              type="number"
              value={formData.attendeeCount}
              onChange={(e) => handleChange('attendeeCount', e.target.value)}
              placeholder="Enter number of attendees"
              min="1"
              max={selectedRoom?.capacity || 999}
              className={`form-input ${errors.attendeeCount ? 'error' : ''}`}
            />
            {errors.attendeeCount && <span className="error-text">{errors.attendeeCount}</span>}
            {selectedRoom?.capacity && (
              <span className="helper-text">Room capacity: {selectedRoom.capacity}</span>
            )}
          </div>

          {/* Recurring Booking Toggle */}
          <div className="form-section recurring-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={formData.isRecurring}
                onChange={(e) => handleChange('isRecurring', e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-switch"></span>
              <MdRepeat className="toggle-icon" />
              <span>Make this a recurring booking</span>
            </label>
            <span className="helper-text">Schedule this booking to repeat weekly</span>
          </div>

          {/* Recurring Options */}
          {formData.isRecurring && (
            <div className="recurring-section">
              <div className="form-section">
                <label className="form-label">Select Days <span className="required">*</span></label>
                <div className="days-selector">
                  {daysOfWeekOptions.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      className={`day-btn ${formData.daysOfWeek.includes(day.value) ? 'selected' : ''}`}
                      onClick={() => {
                        const newDays = formData.daysOfWeek.includes(day.value)
                          ? formData.daysOfWeek.filter(d => d !== day.value)
                          : [...formData.daysOfWeek, day.value];
                        handleChange('daysOfWeek', newDays);
                      }}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                {errors.daysOfWeek && <span className="error-text">{errors.daysOfWeek}</span>}
              </div>

              <div className="form-section">
                <label className="form-label">Repeat Until <span className="required">*</span></label>
                <DatePicker
                  selected={formData.recurringEndDate}
                  onChange={(date) => handleChange('recurringEndDate', date)}
                  minDate={formData.startDateTime || new Date()}
                  maxDate={formData.startDateTime ? new Date(new Date(formData.startDateTime).setDate(new Date(formData.startDateTime).getDate() + 7)) : null}
                  dateFormat="MMM d, yyyy"
                  placeholderText="Select end date"
                  className={`form-input ${errors.recurringEndDate ? 'error' : ''}`}
                />
                {errors.recurringEndDate && <span className="error-text">{errors.recurringEndDate}</span>}
              </div>
            </div>
          )}

          {/* Info Notice */}
          <div className="info-notice">
            <MdEventNote />
            <span>Your booking request will be sent to admin for approval.</span>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || (formData.startDateTime && formData.endDateTime && !isAvailable)}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default BookingModal;
